/**
 * Heart-bar UI — pinned to the top-left corner of the viewport.
 *
 * Layout: a paper-craft background frame (`heartbar_bg.png`) with six
 * half-heart cutouts laid out as `[L][R][L][R][L][R]` — three full
 * hearts side by side. Lives count from 1..6; losing a life animates
 * the rightmost remaining half-heart away.
 *
 * Lose-life animation: heart swings left/right twice (like recoil from
 * a hit), then rotates and falls off the bottom of the screen with
 * fade. After ~1s the element is set display:none so it doesn't
 * occupy layout space — keeping the bar tight as hearts disappear.
 */

const FULL_HEARTS = 3;
const HALF_HEARTS_TOTAL = FULL_HEARTS * 2;

/** Order in which DOM-indexed halves are removed when losing lives.
 *  Walks pair-by-pair (rightmost-visual pair first because the
 *  negative margins put DOM index 0 on the right of the screen),
 *  and within each pair removes the RIGHT half (odd index) first,
 *  then the LEFT half (even index). For 3 hearts this is
 *  [1, 0, 3, 2, 5, 4]. */
const LOSE_ORDER: number[] = (() => {
  const order: number[] = [];
  for (let p = 0; p < FULL_HEARTS; p++) {
    order.push(p * 2 + 1); // R first
    order.push(p * 2);     // then L
  }
  return order;
})();

export class HeartsUI {
  private root: HTMLElement;
  private halves: HTMLImageElement[]; // 6 elements, left → right
  private currentLives: number;

  constructor(initialLives = HALF_HEARTS_TOTAL) {
    let el = document.getElementById('heart-bar') as HTMLElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = 'heart-bar';
      el.className = 'heart-bar hidden';

      const bg = document.createElement('img');
      bg.className = 'heart-bar-bg';
      bg.src = '/img/heartbar_bg.png';
      bg.alt = '';
      bg.draggable = false;
      el.appendChild(bg);

      const row = document.createElement('div');
      row.className = 'heart-bar-row';
      // Three pairs of left+right half-hearts in left-to-right order.
      // Index 0 is the leftmost half (first heart's left); index 5 is
      // the rightmost half (third heart's right). Lives are removed
      // from the right, so index 5 disappears first.
      for (let pair = 0; pair < FULL_HEARTS; pair++) {
        const left = document.createElement('img');
        left.className = 'heart-half heart-half-left';
        left.src = '/img/leftheart.png';
        left.alt = '';
        left.draggable = false;
        row.appendChild(left);

        const right = document.createElement('img');
        right.className = 'heart-half heart-half-right';
        right.src = '/img/rightheart.png';
        right.alt = '';
        right.draggable = false;
        row.appendChild(right);
      }
      el.appendChild(row);

      document.body.appendChild(el);
    }
    this.root = el;
    this.halves = Array.from(el.querySelectorAll('.heart-half')) as HTMLImageElement[];
    this.currentLives = Math.max(0, Math.min(HALF_HEARTS_TOTAL, initialLives));
    this.syncVisibility();
  }

  show() {
    this.root.classList.remove('hidden');
    void this.root.offsetWidth; // reflow so the fade-in transition fires
    this.root.classList.add('visible');
  }
  hide() {
    this.root.classList.remove('visible');
    this.root.classList.add('hidden');
  }

  /** Current life count (0..HALF_HEARTS_TOTAL). */
  get lives(): number { return this.currentLives; }

  /** Hard-set life count without animating. Used for resets / replays. */
  setLives(n: number) {
    this.currentLives = Math.max(0, Math.min(HALF_HEARTS_TOTAL, n));
    // Wipe any in-flight lost animations so resets are clean.
    for (const half of this.halves) {
      half.classList.remove('lost');
      half.style.visibility = '';
      half.style.display = '';
    }
    this.syncVisibility();
  }

  /** Take one life. Animates the next half in LOSE_ORDER (right-half
   *  of each heart first, then left-half, walking rightmost pair to
   *  leftmost) and decrements the count.
   *
   *  Hides via visibility:hidden, NOT display:none, so the slot keeps
   *  its place in the flex layout and the surviving hearts don't
   *  shift to fill the gap. */
  loseLife() {
    if (this.currentLives <= 0) return;
    const lossesSoFar = HALF_HEARTS_TOTAL - this.currentLives;
    const idxToLose = LOSE_ORDER[lossesSoFar];
    const heart = this.halves[idxToLose];
    if (!heart) return;
    heart.classList.add('lost');
    setTimeout(() => {
      heart.style.visibility = 'hidden';
      heart.classList.remove('lost');
    }, 1000);
    this.currentLives--;
  }

  /** Hide hearts beyond currentLives without animating. Hides exactly
   *  the halves that WOULD have been lost so far given the current
   *  life count, using LOSE_ORDER. visibility:hidden keeps the slots
   *  in flex layout so survivors don't reflow. */
  private syncVisibility() {
    const lossesSoFar = HALF_HEARTS_TOTAL - this.currentLives;
    const hidden = new Set(LOSE_ORDER.slice(0, lossesSoFar));
    for (let i = 0; i < this.halves.length; i++) {
      this.halves[i].style.visibility = hidden.has(i) ? 'hidden' : '';
    }
  }
}
